"use client";
import { StudioProvider, useStudio } from "../../hooks/useStudio";
import React, { useEffect, useMemo, useState } from "react";

import { cn, imageToDataUri } from "../../lib/utils";
import {
  ConfiguratorProvider,
  useConfiguratorContext,
} from "../../hooks/configurator/configurator-context";
import { ConfiguratorConfig, Section } from "../../hooks/configurator/types";
import SelectionHelperDialog from "./SelectionHelperDialog";
import { MobileWarningPopup } from "./MobileWarningPopup";
import { ConfiguratorHeader } from "./ConfiguratorHeader";
import { LoadingScreen } from "./LoadingScreen";
import { ViewerControls } from "./ViewerControls";
import { CameraControls } from "./CameraControls";
import { ConfigurationPanel } from "./ConfigurationPanel";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerClose,
} from "../ui/drawer";
import { useIsScreenWidth } from "../../hooks/useScreenWidth";
import { ThemeProvider } from "../theme-provider";
import { ListFilter } from "lucide-react";
import { Button } from "../ui/button";

interface ConfiguratorProps {
  modelName: string;
  config: ConfiguratorConfig;
}

function Configurator(props: ConfiguratorProps) {
  const { modelName, config } = props;

  const studio = useStudio({
    baseUrl: "https://meet.studio3dx.com/v2/scene",
    modelId: config.studioSceneId,
  });

  const [loaded, setLoaded] = useState(false);
  const [fadeOut, setFadeOut] = useState(false);
  const [fullScreen, setFullScreen] = useState(false);
  const [totalPrice, setTotalPrice] = useState(0);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [showMobilePopup, setShowMobilePopup] = useState(false);
  const [isRendering, setIsRendering] = useState(false);
  const [isMobileDrawerOpen, setIsMobileDrawerOpen] = useState(false);

  const configurator = useConfiguratorContext();

  // Define breakpoints
  const MOBILE_BREAKPOINT = 768;
  const TABLET_BREAKPOINT = 1024;

  // Use the custom hook to check screen width
  const isMobile = useIsScreenWidth(MOBILE_BREAKPOINT);
  const isTablet = useIsScreenWidth(TABLET_BREAKPOINT) && !isMobile;
  const shouldShowDrawer = isMobile || isTablet;
  const drawerDirection = isMobile ? "bottom" : "right";

  const defaultCameraTransform = isMobile
    ? config.defaultMobileCameraTransform
    : config.defaultCameraTransform;

  const toggleMobileDrawer = () => {
    setIsMobileDrawerOpen(!isMobileDrawerOpen);
  };

  const resetCamera = () => {
    if (studio.loaded && defaultCameraTransform) {
      studio.viewer.setCameraLookAt(...defaultCameraTransform, true);
    }
  };

  useEffect(() => {
    // Show mobile popup if mobile
    setShowMobilePopup(isMobile);
  }, [isMobile]);

  // Simulate loading progress
  useEffect(() => {
    if (!loaded && !studio.loaded) {
      const interval = setInterval(() => {
        setLoadingProgress((prev) => {
          const newProgress = prev + Math.random() * 5;
          return newProgress > 90 ? 90 : newProgress;
        });
      }, 200);

      return () => clearInterval(interval);
    }

    if (studio.loaded && loadingProgress < 100) {
      setLoadingProgress(100);
    }
  }, [loaded, studio.loaded, loadingProgress]);

  const updateVisibility = () => {
    if (!studio.loaded) return;

    // Object to store visibility states for batch processing
    const visibilityUpdates: { [key: string]: boolean } = {};

    // First, hide all objects
    for (const section of configurator.getAllSections()) {
      for (const option of section.options) {
        const ids = [option?.id];
        if (option.metadata?.viewerObjects)
          ids.push(...option.metadata.viewerObjects);

        for (const object of ids) {
          visibilityUpdates[object] = false; // Mark object as hidden initially
        }
      }
    }

    // Then, show only enabled options
    for (const section of configurator.getAllSections()) {
      for (const option of section.options) {
        const selected = configurator.isOptionSelected(option.id);
        if (!selected) continue;

        const ids = [option?.id];
        if (option.metadata?.viewerObjects)
          ids.push(...option.metadata.viewerObjects);

        for (const object of ids) {
          visibilityUpdates[object] = true; // Mark object as visible if selected
        }

        // Hide specific objects if needed for selected options
        if (option?.metadata?.hideViewerObjects) {
          for (const object of option.metadata.hideViewerObjects) {
            visibilityUpdates[object] = false; // Mark object as hidden
          }
        }

        // Update materials for selected options
        if (option?.metadata?.viewerMaterials) {
          for (const [name, config] of Object.entries(
            option.metadata.viewerMaterials
          ) as any) {
            const materialConfig = {
              ...config,
              map: config.map ? studio.getTexture(config.map) : undefined,
            };

            // Handle special cases
            // TODO: find a better way to do this
            if (option?.metadata?.customColourSourceSectionId) {
              for (const section of configurator.getAllSections()) {
                if (
                  section.id === option?.metadata?.customColourSourceSectionId
                ) {
                  const selectedOptions = configurator.getSelectedOptions(
                    section.id
                  );

                  if (selectedOptions.length > 0) {
                    const matchOption = selectedOptions[0];
                    if (matchOption?.metadata?.viewerMaterials) {
                      for (const [name, config] of Object.entries(
                        matchOption.metadata.viewerMaterials
                      ) as any) {
                        if (
                          name ===
                          option?.metadata?.customColourSourceMaterialName
                        ) {
                          materialConfig.color = config.color;
                        }
                      }
                    }
                  }
                }
              }
            }

            // Apply material
            studio.viewer.setMaterial(name, materialConfig);
          }
        }
      }
    }

    // Apply all visibility updates in a single batch call
    studio.viewer.setObjectVisibility(visibilityUpdates);

    // Update shadows once after all visibility and material changes
    studio.viewer.updateShadows();
  };

  useEffect(() => {
    updateVisibility();

    // Calculate total price
    let price = 0;
    for (const section of configurator.getAllSections()) {
      for (const option of section.options) {
        if (configurator.isOptionSelected(option.id) && option.price) {
          price += option.price;
        }
      }
    }
    setTotalPrice(price);
  }, [configurator.selectedOptions]);

  // useNavigationPrompt("Are you sure you want to leave without saving?", true);

  useEffect(() => {
    if (!loaded && studio.loaded) {
      const init = async () => {
        // Load textures
        await Promise.all(
          Object.entries(config.textures).map(async ([name, uri]) => {
            const dataUrl = (await imageToDataUri(uri)) as string;
            studio.addTexture(name, dataUrl);
          })
        );

        // Load materials
        if (config.materials) {
          for (const [name, material] of Object.entries(
            config.materials
          ) as any) {
            const materialConfig = {
              ...material,
              map: material.map ? studio.getTexture(material.map) : undefined,
            };
            studio.viewer.setMaterial(name, materialConfig);
          }
        }

        studio.viewer.setCameraConfig({
          maxDistance: 30,
          minDistance: 8,
          maxPolarAngle: Math.PI / 2 - 0.1,
          truckSpeed: 0,
          fov: 30,
        });

        studio.viewer.setCameraLookAt(...defaultCameraTransform, true);
        studio.viewer.setMSAASamples(4);
        studio.viewer.setSMAAEnabled(true);

        updateVisibility();
      };

      init().then(() => {
        // Start fade-out animation
        setFadeOut(true);
        // Wait for animation to complete before setting loaded to true
        setTimeout(() => {
          setLoaded(true);
        }, 500); // 500ms matches the CSS transition duration
      });
    }
  }, [loaded, studio.loaded]);

  const toggleFullScreen = () => {
    setFullScreen(!fullScreen);
  };

  const takeScreenshot = async () => {
    if (!loaded || !studio.viewer || isRendering) return;

    setIsRendering(true);

    try {
      // Get current camera position
      const camera = await new Promise<any>((resolve) => {
        studio.viewer.getCamera(resolve);
      });

      // Responsive render parameters - lower resolution on mobile to prevent memory issues
      const renderParams = {
        width: isMobile ? 1920 : 4160, // Mobile: Full HD (1920x1080), Desktop: 4K (4160x2340)
        height: isMobile ? 1080 : 2340, // 16:9 aspect ratio maintained on both
        fov: 30,
        format: "image/jpeg" as const,
        quality: isMobile ? 0.8 : 0.9, // Lower quality on mobile for smaller file size
        transparent: false,
        fit: false,
        position: camera.position,
        target: camera.target,
      };

      // Render the image
      const result = await new Promise<Blob>((resolve) => {
        studio.viewer.renderOffscreenFrame(renderParams, resolve);
      });

      // Create download link
      const url = URL.createObjectURL(result);
      const timestamp = new Date();

      // Create a clean filename with better timestamp formatting
      const modelDisplayName = modelName.toLowerCase().replace(/\s+/g, "-");

      // Format timestamp as YYYY-MM-DD_HH-MM-SS for better readability
      const year = timestamp.getFullYear();
      const month = String(timestamp.getMonth() + 1).padStart(2, "0");
      const day = String(timestamp.getDate()).padStart(2, "0");
      const hours = String(timestamp.getHours()).padStart(2, "0");
      const minutes = String(timestamp.getMinutes()).padStart(2, "0");
      const seconds = String(timestamp.getSeconds()).padStart(2, "0");

      const timestampStr = `${year}-${month}-${day}_${hours}-${minutes}-${seconds}`;
      const filename = `caroffshore-${modelDisplayName}-${timestampStr}.jpg`;

      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Screenshot failed:", error);
    } finally {
      setIsRendering(false);
    }
  };

  return (
    <>
      <main className="flex flex-col w-screen h-screen select-none bg-linear-to-br from-gray-50 to-white">
        {/* <MobileWarningPopup
          isVisible={showMobilePopup}
          onClose={() => setShowMobilePopup(false)}
        /> */}
        <SelectionHelperDialog />
        <ConfiguratorHeader
          isMobile={isMobile}
          showDrawerToggle={shouldShowDrawer}
          onToggleMobileDrawer={toggleMobileDrawer}
        />

        {/* Mobile/Tablet Drawer */}
        {shouldShowDrawer && (
          <Drawer
            direction={drawerDirection}
            open={isMobileDrawerOpen}
            onOpenChange={setIsMobileDrawerOpen}
          >
            <DrawerContent
              className={cn(
                drawerDirection === "bottom"
                  ? "max-h-[85vh]"
                  : "max-w-md w-full"
              )}
            >
              <DrawerHeader className="bg-white border-b border-gray-200 flex-row items-center justify-between">
                <DrawerTitle className="text-lg font-semibold text-gray-900">
                  Configuration
                </DrawerTitle>
                <DrawerClose className="p-2 rounded-md hover:bg-gray-100 transition-colors">
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </DrawerClose>
              </DrawerHeader>

              <div className="px-4 pb-4 overflow-y-auto flex-1">
                <ConfigurationPanel
                  config={config}
                  fullScreen={false}
                  totalPrice={totalPrice}
                  isMobileDrawer={true}
                />
              </div>
            </DrawerContent>
          </Drawer>
        )}

        <div
          className={cn(
            "flex grow w-full h-full overflow-hidden relative",
            shouldShowDrawer ? "flex-col" : "flex-row",
            fullScreen ? "flex-col" : ""
          )}
        >
          <div
            className={cn(
              "grow z-10 flex flex-col gap-4 bg-white relative",
              shouldShowDrawer ? "basis-full" : "flex-1",
              fullScreen ? "basis-full" : ""
            )}
          >
            <div className="overflow-hidden h-full flex grow relative portrait:rounded-none">
              <LoadingScreen isVisible={!loaded} progress={loadingProgress} />

              {/* Screenshot Overlay */}
              {isRendering && (
                <div className="absolute inset-0 bg-white/50 backdrop-blur-2xl z-30 flex items-center justify-center transition-opacity duration-300">
                  <div className="bg-white rounded-lg px-6 py-4 shadow-lg border border-gray-200">
                    <div className="flex items-center gap-3">
                      <div className="animate-spin rounded-full h-6 w-6 border-2 border-gray-300 border-t-blue-600"></div>
                      <span className="text-gray-700 font-medium">
                        Taking Screenshot...
                      </span>
                    </div>
                  </div>
                </div>
              )}

              <ViewerControls
                fullScreen={fullScreen}
                onToggleFullScreen={toggleFullScreen}
                onTakeScreenshot={takeScreenshot}
                onResetCamera={resetCamera}
                isRendering={isRendering}
                isMobile={isMobile}
              />

              {/* Floating Options Button for Mobile/Tablet */}
              {shouldShowDrawer && (
                <Button
                  onClick={toggleMobileDrawer}
                  className={cn(
                    "fixed bottom-6 left-1/2 -translate-x-1/2 z-20",
                    "shadow-lg hover:shadow-xl transition-all duration-200",
                    "bg-white hover:bg-gray-50 text-gray-900 border border-gray-200",
                    "rounded-full px-6 py-3 h-auto",
                    "flex items-center gap-2"
                  )}
                  variant="outline"
                >
                  <ListFilter className="h-5 w-5" />
                  <span className="font-medium">Options</span>
                </Button>
              )}

              <iframe ref={studio.iframeRef} className="min-h-full w-full" />
            </div>
          </div>
          {/* Desktop Configuration Panel */}
          {!shouldShowDrawer && (
            <ConfigurationPanel
              config={config}
              fullScreen={fullScreen}
              totalPrice={totalPrice}
              isMobileDrawer={false}
            />
          )}
        </div>
      </main>
    </>
  );
}

const ConfiguratorWrapper = (props: ConfiguratorProps) => (
  <StudioProvider>
    <ConfiguratorProvider
      sections={props.config.sections}
      configuratorName={props.modelName}
    >
      <Configurator {...props} />
    </ConfiguratorProvider>
  </StudioProvider>
);

export default ConfiguratorWrapper;
